import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { llm, needsUserAction, normalizeError, sessionCredentials } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Owns"can this account call a model, and with which one".
 *
 * The key is requested just in time: the first time an AI action is invoked,
 * not at login. Everything that does not need a model — importing, outlining,
 * editing, versioning, exporting — stays usable without one.
 */
const LLMCtx = createContext(null);

export function LLMProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ ready: false, loading: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogReason, setDialogReason] = useState(null);
  // The last failure that is not about credentials, shown in its own dialog.
  const [failure, setFailure] = useState(null);
  const pending = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus({ ready: false, loading: false });
      return null;
    }
    try {
      const s = await llm.status();
      setStatus({ ...s, loading: false });
      return s;
    } catch {
      setStatus({ ready: false, loading: false });
      return null;
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openKeyDialog = useCallback((reason = null) => {
    setDialogReason(reason);
    setDialogOpen(true);
  }, []);

  /**
   * Gate an AI action. Resolves true once a working key is configured,
   * false if the user dismissed the dialog.
   */
  const ensureKey = useCallback(async () => {
    if (status.ready) return true;
    const fresh = await refresh();
    if (fresh?.ready) return true;
    openKeyDialog({ code: "no_key" });
    return new Promise((resolve) => {
      pending.current = resolve;
    });
  }, [status.ready, refresh, openKeyDialog]);

  const resolvePending = useCallback((value) => {
    const resolve = pending.current;
    pending.current = null;
    resolve?.(value);
  }, []);

  const handleDialogChange = useCallback(
    (open) => {
      setDialogOpen(open);
      if (!open) resolvePending(false);
    },
    [resolvePending]
  );

  const onKeyConfigured = useCallback(
    async (result) => {
      if (!result.stored) {
        // Session-only: the browser holds it and sends it per request.
        sessionCredentials.set({
 provider: result.provider,
          api_key: result.api_key,
 model: result.model,
          base_url: result.base_url,
        });
      }
      setDialogOpen(false);
      await refresh();
      resolvePending(true);
    },
    [refresh, resolvePending]
  );

  /**
   * Called when a request failed. Returns the normalised error, and reopens
   * the key dialog when the user is the only one who can fix it.
   */
  const reportError = useCallback(
    (err) => {
      const normalized = normalizeError(err);
      if (needsUserAction(normalized.code)) {
        /*
          Si dice che serve un modello; non si apre la finestra da soli.

          Aprirla qui significava che bastava una chiamata fallita in
          sottofondo - una pagina che si apre, un suggerimento automatico - per
          far comparire un modulo di configurazione a chi non aveva chiesto
          niente. Ora compare l'errore, con dentro il pulsante per collegarlo:
          la finestra si apre quando la si vuole aprire.
        */
        if (normalized.code !== "no_key") sessionCredentials.clear();
        refresh();
        setFailure({ ...normalized, offreCollegamento: true });
      } else {
        // Everything else gets said in full, in a window that waits to be read.
        // A toast carrying "Request failed with status code 428" told nobody
        // anything, and was gone before it could be copied down.
        setFailure(normalized);
      }
      return normalized;
    },
    [refresh]
  );

  const value = useMemo(
    () => ({
      status,
 ready: status.ready,
      refresh,
      ensureKey,
      openKeyDialog,
      reportError,
      dialogOpen,
      dialogReason,
      handleDialogChange,
      onKeyConfigured,
      failure,
      showError: setFailure,
      dismissError: () => setFailure(null),
    }),
    [status, refresh, ensureKey, openKeyDialog, reportError, dialogOpen, dialogReason, handleDialogChange, onKeyConfigured, failure]
  );

  return <LLMCtx.Provider value={value}>{children}</LLMCtx.Provider>;
}

export const useLLM = () => useContext(LLMCtx);
